import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import RewriteModal from '../components/RewriteModal';

// ============================================================================
// Lexical JSON <-> HTML Conversion Utilities
// ============================================================================

function isLexicalJson(val: any): boolean {
  return val && typeof val === 'object' && val.root && Array.isArray(val.root?.children);
}

function lexicalToHtml(data: any): string {
  if (!data || !data.root || !Array.isArray(data.root.children)) return '';
  return processLexicalChildren(data.root.children);
}

function processLexicalChildren(children: any[]): string {
  if (!Array.isArray(children)) return '';
  return children.map(node => processLexicalNode(node)).join('');
}

function processLexicalNode(node: any): string {
  if (!node || !node.type) return '';
  switch (node.type) {
    case 'paragraph':
      return `<p>${processLexicalChildren(node.children || [])}</p>`;
    case 'heading': {
      const tag = node.tag || 'h2';
      return `<${tag}>${processLexicalChildren(node.children || [])}</${tag}>`;
    }
    case 'text': {
      let text = (node.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      if (!text) return '';
      const fmt = node.format || 0;
      if (fmt & 1) text = `<strong>${text}</strong>`;
      if (fmt & 2) text = `<em>${text}</em>`;
      if (fmt & 4) text = `<s>${text}</s>`;
      if (fmt & 8) text = `<u>${text}</u>`;
      if (fmt & 16) text = `<code>${text}</code>`;
      return text;
    }
    case 'linebreak':
      return '<br>';
    case 'list': {
      const tag = node.listType === 'number' ? 'ol' : 'ul';
      return `<${tag}>${processLexicalChildren(node.children || [])}</${tag}>`;
    }
    case 'listitem':
      return `<li>${processLexicalChildren(node.children || [])}</li>`;
    case 'link':
      return `<a href="${node.url || '#'}">${processLexicalChildren(node.children || [])}</a>`;
    case 'autolink':
      return `<a href="${node.url || '#'}">${processLexicalChildren(node.children || [])}</a>`;
    case 'quote':
      return `<blockquote>${processLexicalChildren(node.children || [])}</blockquote>`;
    default:
      if (node.children) return processLexicalChildren(node.children);
      return '';
  }
}

function htmlToLexical(html: string): any {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const children = Array.from(doc.body.childNodes)
    .map(n => domToLexical(n))
    .flat()
    .filter(Boolean);
  return {
    root: {
      children: children.length > 0 ? children : [
        { type: 'paragraph', children: [], direction: 'ltr', format: '', indent: 0, version: 1 }
      ],
      direction: 'ltr', format: '', indent: 0, type: 'root', version: 1
    }
  };
}

function domToLexical(node: Node): any[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    if (!text) return [];
    return [{ type: 'text', text, format: 0, mode: 'normal', style: '', detail: 0, version: 1 }];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return [];
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const kids = Array.from(el.childNodes).map(n => domToLexical(n)).flat().filter(Boolean);

  switch (tag) {
    case 'p':
      return [{ type: 'paragraph', children: kids, direction: 'ltr', format: '', indent: 0, version: 1 }];
    case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6':
      return [{ type: 'heading', tag, children: kids, direction: 'ltr', format: '', indent: 0, version: 1 }];
    case 'strong': case 'b':
      kids.forEach((k: any) => { if (k.type === 'text') k.format = (k.format || 0) | 1; });
      return kids;
    case 'em': case 'i':
      kids.forEach((k: any) => { if (k.type === 'text') k.format = (k.format || 0) | 2; });
      return kids;
    case 's': case 'del':
      kids.forEach((k: any) => { if (k.type === 'text') k.format = (k.format || 0) | 4; });
      return kids;
    case 'u':
      kids.forEach((k: any) => { if (k.type === 'text') k.format = (k.format || 0) | 8; });
      return kids;
    case 'ul':
      return [{ type: 'list', listType: 'bullet', children: kids, direction: 'ltr', format: '', indent: 0, start: 1, tag: 'ul', version: 1 }];
    case 'ol':
      return [{ type: 'list', listType: 'number', children: kids, direction: 'ltr', format: '', indent: 0, start: 1, tag: 'ol', version: 1 }];
    case 'li':
      return [{ type: 'listitem', children: kids, direction: 'ltr', format: '', indent: 0, value: 1, version: 1 }];
    case 'a':
      return [{ type: 'link', url: el.getAttribute('href') || '#', children: kids, direction: 'ltr', format: '', indent: 0, version: 2 }];
    case 'blockquote':
      return [{ type: 'quote', children: kids, direction: 'ltr', format: '', indent: 0, version: 1 }];
    case 'br':
      return [{ type: 'linebreak', version: 1 }];
    default:
      if (kids.length > 0) {
        const hasBlock = kids.some((k: any) => ['paragraph', 'heading', 'list', 'quote'].includes(k.type));
        if (hasBlock) return kids;
        return [{ type: 'paragraph', children: kids, direction: 'ltr', format: '', indent: 0, version: 1 }];
      }
      return [];
  }
}

// Extract plain text from Lexical JSON for preview
function lexicalToPlainText(data: any): string {
  if (!data || !data.root) return '';
  return extractText(data.root.children || []).trim();
}

function extractText(children: any[]): string {
  if (!Array.isArray(children)) return '';
  return children.map((node: any) => {
    if (node.type === 'text') return node.text || '';
    if (node.type === 'linebreak') return '\n';
    if (node.type === 'paragraph') return extractText(node.children || []) + '\n';
    if (node.children) return extractText(node.children);
    return '';
  }).join('');
}

const quillStyles = `
  .ql-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    min-height: 150px;
  }
  .ql-editor {
    min-height: 150px;
    padding: 12px 15px;
  }
  .ql-editor p { margin-bottom: 1em; }
  .ql-toolbar {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: #f8f9fa;
  }
  .ql-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

interface PayloadCredential {
  id: number;
  project_id: number;
  cms_url: string;
  tenant_id: string;
  is_verified: boolean;
  cached_page_count: number | null;
  created_at: string;
  updated_at: string;
}

interface PayloadPage {
  id: string;
  title: string;
  slug: string | null;
  status: string | null;
  updated_at: string | null;
}

interface ContentField {
  id: string;
  key: string;
  fieldType: string;
  value: any;
  field_group_title: string | null;
  field_group_slug: string | null;
}

interface LayoutBlock {
  id: string;
  blockType: string;
  blockName: string | null;
  data: Record<string, any>;
}

interface PageDetailResponse {
  id: string;
  title: string;
  slug: string | null;
  customCSS: string | null;
  customJavaScript: string | null;
  seo: Record<string, any> | null;
  content_fields: ContentField[];
  field_groups: string[];
  field_count: number;
  layout_blocks: LayoutBlock[];
  page_settings: Record<string, any> | null;
}

interface UpdateSession {
  id: number;
  project_id: number;
  page_id: string;
  page_title: string | null;
  action: string;
  fields_updated: string[] | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

type Step = 'credentials' | 'pages' | 'editor';
type EditorTab = 'content' | 'layout' | 'seo' | 'code';

export default function PayloadAgentPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [step, setStep] = useState<Step>('credentials');

  // Credentials
  const [credential, setCredential] = useState<PayloadCredential | null>(null);
  const [cmsUrl, setCmsUrl] = useState('https://cms-staging.brandnorth.dev');
  const [apiKey, setApiKey] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; total_pages?: number | null } | null>(null);

  // Pages list
  const [pages, setPages] = useState<PayloadPage[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Editor
  const [editingPage, setEditingPage] = useState<PayloadPage | null>(null);
  const [pageDetail, setPageDetail] = useState<PageDetailResponse | null>(null);
  const [contentFields, setContentFields] = useState<ContentField[]>([]);
  const [originalContentFields, setOriginalContentFields] = useState<ContentField[]>([]);
  const [pageTitle, setPageTitle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [pageSlug, setPageSlug] = useState('');
  const [originalSlug, setOriginalSlug] = useState('');
  const [customCSS, setCustomCSS] = useState('');
  const [originalCSS, setOriginalCSS] = useState('');
  const [customJS, setCustomJS] = useState('');
  const [originalJS, setOriginalJS] = useState('');
  const [seoFields, setSeoFields] = useState<Record<string, any>>({});
  const [originalSeo, setOriginalSeo] = useState<Record<string, any>>({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorSuccess, setEditorSuccess] = useState<string | null>(null);
  const [editorTab, setEditorTab] = useState<EditorTab>('content');
  const [activeGroup, setActiveGroup] = useState<string>('all');
  const [codeMode, setCodeMode] = useState<Record<string, boolean>>({});

  // Rewrite modal state
  const [rewriteFieldId, setRewriteFieldId] = useState<string | null>(null);
  const [rewriteText, setRewriteText] = useState('');
  const [rewriteIsLexical, setRewriteIsLexical] = useState(false);

  // Layout blocks
  const [layoutBlocks, setLayoutBlocks] = useState<LayoutBlock[]>([]);
  const [originalLayoutBlocks, setOriginalLayoutBlocks] = useState<LayoutBlock[]>([]);
  const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});

  // Page settings
  const [pageSettings, setPageSettings] = useState<Record<string, any>>({});
  const [originalPageSettings, setOriginalPageSettings] = useState<Record<string, any>>({});

  // History
  const [history, setHistory] = useState<UpdateSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Media browser
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [mediaFieldTarget, setMediaFieldTarget] = useState<string | null>(null);
  const [mediaItems, setMediaItems] = useState<any[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaPage, setMediaPage] = useState(1);
  const [mediaTotalPages, setMediaTotalPages] = useState(1);

  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }), []);

  const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];

  useEffect(() => {
    if (projectId) {
      loadCredentials();
      loadProjectInfo();
    }
  }, [projectId]);

  const loadProjectInfo = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProjectName(response.data.name || response.data.client_name || `Project #${projectId}`);
    } catch { setProjectName(`Project #${projectId}`); }
  };

  const loadCredentials = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/payloadagent/credentials/${projectId}`);
      setCredential(response.data);
      setCmsUrl(response.data.cms_url);
      setTenantId(response.data.tenant_id);
      setStep('pages');
      loadPages(1);
    } catch (err: any) {
      if (err.response?.status === 404) setStep('credentials');
    }
  };

  const testConnection = async () => {
    if (!cmsUrl || !tenantId) { setError('Please fill in CMS URL and Tenant ID'); return; }
    setTesting(true); setTestResult(null); setError(null);
    try {
      const response = await api.post('/payloadagent/credentials/test', { cms_url: cmsUrl, api_key: apiKey || null, tenant_id: tenantId });
      setTestResult(response.data);
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Connection test failed' });
    } finally { setTesting(false); }
  };

  const saveCredentials = async () => {
    if (!projectId || !cmsUrl || !tenantId) { setError('Please fill in required fields'); return; }
    setSaving(true); setError(null);
    try {
      const response = await api.post(`/payloadagent/credentials/${projectId}`, { cms_url: cmsUrl, api_key: apiKey || null, tenant_id: tenantId });
      setCredential(response.data); setApiKey(''); setStep('pages'); loadPages(1);
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to save credentials'); }
    finally { setSaving(false); }
  };

  const deleteCredentials = async () => {
    if (!projectId || !confirm('Are you sure you want to delete these credentials?')) return;
    try {
      await api.delete(`/payloadagent/credentials/${projectId}`);
      setCredential(null); setCmsUrl('https://cms-staging.brandnorth.dev'); setApiKey(''); setTenantId(''); setStep('credentials');
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to delete credentials'); }
  };

  const loadPages = async (page: number, search?: string) => {
    if (!projectId) return;
    setLoadingPages(true); setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: '20' });
      if (search) params.append('search', search);
      const response = await api.get(`/payloadagent/pages/${projectId}?${params.toString()}`);
      setPages(response.data.pages); setTotalDocs(response.data.total);
      setCurrentPage(response.data.page); setTotalPages(response.data.total_pages);
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to load pages'); }
    finally { setLoadingPages(false); }
  };

  const openEditor = async (page: PayloadPage) => {
    if (!projectId) return;
    setEditingPage(page); setLoadingFields(true); setEditorError(null); setEditorSuccess(null); setStep('editor'); setEditorTab('content');
    try {
      const response = await api.get<PageDetailResponse>(`/payloadagent/pages/${projectId}/${page.id}`);
      const data = response.data;
      setPageDetail(data);
      setContentFields(data.content_fields);
      setOriginalContentFields(JSON.parse(JSON.stringify(data.content_fields)));
      setPageTitle(data.title); setOriginalTitle(data.title);
      setPageSlug(data.slug || ''); setOriginalSlug(data.slug || '');
      setCustomCSS(data.customCSS || ''); setOriginalCSS(data.customCSS || '');
      setCustomJS(data.customJavaScript || ''); setOriginalJS(data.customJavaScript || '');
      setSeoFields(data.seo || {}); setOriginalSeo(JSON.parse(JSON.stringify(data.seo || {})));
      setLayoutBlocks(data.layout_blocks || []);
      setOriginalLayoutBlocks(JSON.parse(JSON.stringify(data.layout_blocks || [])));
      setPageSettings(data.page_settings || {}); setOriginalPageSettings(JSON.parse(JSON.stringify(data.page_settings || {})));
      setExpandedBlocks({});
      if (data.field_groups.length > 0) setActiveGroup(data.field_groups[0]);
      else setActiveGroup('all');
    } catch (err: any) { setEditorError(err.response?.data?.detail || 'Failed to load page fields'); }
    finally { setLoadingFields(false); }
  };

  const closeEditor = () => {
    setEditingPage(null); setPageDetail(null); setContentFields([]); setOriginalContentFields([]);
    setLayoutBlocks([]); setOriginalLayoutBlocks([]); setPageSettings({}); setOriginalPageSettings({});
    setEditorError(null); setEditorSuccess(null); setStep('pages');
  };

  const handleContentFieldChange = (fieldId: string, newValue: any) => {
    setContentFields(prev => prev.map(f => f.id === fieldId ? { ...f, value: newValue } : f));
  };

  const handleSeoChange = (key: string, value: any) => {
    setSeoFields(prev => ({ ...prev, [key]: value }));
  };

  // Compute changes
  const hasContentChanges = JSON.stringify(contentFields) !== JSON.stringify(originalContentFields);
  const hasTitleChange = pageTitle !== originalTitle;
  const hasSlugChange = pageSlug !== originalSlug;
  const hasCSSChange = customCSS !== originalCSS;
  const hasJSChange = customJS !== originalJS;
  const hasSeoChange = JSON.stringify(seoFields) !== JSON.stringify(originalSeo);
  const hasLayoutChanges = JSON.stringify(layoutBlocks) !== JSON.stringify(originalLayoutBlocks);
  const hasSettingsChanges = JSON.stringify(pageSettings) !== JSON.stringify(originalPageSettings);
  const hasChanges = hasContentChanges || hasTitleChange || hasSlugChange || hasCSSChange || hasJSChange || hasSeoChange || hasLayoutChanges || hasSettingsChanges;

  const modifiedFieldCount = contentFields.filter((f, i) =>
    JSON.stringify(f.value) !== JSON.stringify(originalContentFields[i]?.value)
  ).length + (hasTitleChange ? 1 : 0) + (hasSlugChange ? 1 : 0) + (hasCSSChange ? 1 : 0) + (hasJSChange ? 1 : 0) + (hasSeoChange ? 1 : 0)
    + (hasLayoutChanges ? 1 : 0) + (hasSettingsChanges ? 1 : 0);

  const savePageFields = async () => {
    if (!projectId || !editingPage) return;
    setSavingFields(true); setEditorError(null); setEditorSuccess(null);
    try {
      const payload: any = {};
      if (hasTitleChange) payload.title = pageTitle;
      if (hasSlugChange) payload.slug = pageSlug;
      if (hasCSSChange) payload.customCSS = customCSS;
      if (hasJSChange) payload.customJavaScript = customJS;
      if (hasSeoChange) payload.seo = seoFields;
      if (hasContentChanges) {
        // Send the full fields array with updated values
        payload.fields = contentFields.map(f => ({
          id: f.id,
          key: f.key,
          fieldType: f.fieldType,
          value: f.value,
          field_group_title: f.field_group_title,
          field_group_slug: f.field_group_slug,
        }));
      }
      if (hasLayoutChanges) {
        // Reconstruct full layout blocks with id, blockType, blockName + data
        payload.layout = layoutBlocks.map(b => ({
          id: b.id,
          blockType: b.blockType,
          blockName: b.blockName,
          ...b.data,
        }));
      }
      if (hasSettingsChanges) {
        if (pageSettings.stickyHeader !== originalPageSettings.stickyHeader) {
          payload.stickyHeader = pageSettings.stickyHeader;
        }
        if (pageSettings.showBackToTop !== originalPageSettings.showBackToTop) {
          payload.showBackToTop = pageSettings.showBackToTop;
        }
      }

      await api.patch(`/payloadagent/pages/${projectId}/${editingPage.id}`, payload);
      // Snapshot current as original
      setOriginalContentFields(JSON.parse(JSON.stringify(contentFields)));
      setOriginalTitle(pageTitle); setOriginalSlug(pageSlug);
      setOriginalCSS(customCSS); setOriginalJS(customJS);
      setOriginalSeo(JSON.parse(JSON.stringify(seoFields)));
      setOriginalLayoutBlocks(JSON.parse(JSON.stringify(layoutBlocks)));
      setOriginalPageSettings(JSON.parse(JSON.stringify(pageSettings)));
      setEditorSuccess('Changes saved successfully!');
      setTimeout(() => setEditorSuccess(null), 3000);
      await loadPages(currentPage, searchTerm);
    } catch (err: any) { setEditorError(err.response?.data?.detail || 'Failed to save changes'); }
    finally { setSavingFields(false); }
  };

  const loadHistory = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/payloadagent/sessions/${projectId}?limit=50`);
      setHistory(response.data.sessions); setShowHistory(true);
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to load history'); }
  };

  const openMediaBrowser = (fieldId: string) => {
    setMediaFieldTarget(fieldId);
    setShowMediaBrowser(true);
    setMediaSearch('');
    setMediaPage(1);
    loadMedia(1);
  };

  const loadMedia = async (page: number, search?: string) => {
    if (!projectId) return;
    setMediaLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: '20' });
      if (search) params.append('search', search);
      const response = await api.get(`/payloadagent/media/${projectId}?${params.toString()}`);
      setMediaItems(response.data.media);
      setMediaPage(response.data.page);
      setMediaTotalPages(response.data.total_pages);
    } catch { setMediaItems([]); }
    finally { setMediaLoading(false); }
  };

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!projectId || !e.target.files?.length) return;
    const file = e.target.files[0];
    setMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/payloadagent/media/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const media = response.data.media;
      if (mediaFieldTarget) {
        handleContentFieldChange(mediaFieldTarget, media.id || media);
      }
      setShowMediaBrowser(false);
      setMediaFieldTarget(null);
    } catch (err: any) {
      setEditorError(err.response?.data?.detail || 'Failed to upload media');
    } finally { setMediaUploading(false); }
  };

  const selectMediaItem = (item: any) => {
    if (mediaFieldTarget) {
      handleContentFieldChange(mediaFieldTarget, item.id || item);
    }
    setShowMediaBrowser(false);
    setMediaFieldTarget(null);
  };

  const getMediaUrl = (val: any): string | null => {
    if (!val) return null;
    if (typeof val === 'object' && val.url) return val.url;
    if (typeof val === 'object' && val.filename && credential) {
      return `${credential.cms_url}/media/${val.filename}`;
    }
    return null;
  };

  // Get filtered fields for the active group
  const visibleFields = activeGroup === 'all'
    ? contentFields
    : contentFields.filter(f => f.field_group_title === activeGroup);

  // Prettify field key into label
  const fieldLabel = (key: string) => {
    // Remove group prefix (e.g., "home_template_hero_heading" -> "hero heading")
    const parts = key.split('_');
    // Find where the group slug ends
    const slug = contentFields.find(f => f.key === key)?.field_group_slug || '';
    const cleaned = slug ? key.replace(slug + '_', '') : key;
    return cleaned.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Render a single content field editor
  const renderFieldEditor = (field: ContentField) => {
    const isModified = JSON.stringify(field.value) !== JSON.stringify(
      originalContentFields.find(f => f.id === field.id)?.value
    );
    const val = field.value;

    return (
      <div key={field.id} className={`p-5 rounded-lg border ${isModified ? 'bg-yellow-50 border-yellow-300' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <label className="font-semibold text-gray-900 text-sm block">
              {fieldLabel(field.key)}
            </label>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-400 font-mono">{field.key}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                field.fieldType === 'richText' ? 'bg-purple-100 text-purple-700' :
                field.fieldType === 'textarea' ? 'bg-blue-100 text-blue-700' :
                field.fieldType === 'repeater' ? 'bg-orange-100 text-orange-700' :
                field.fieldType === 'media' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {field.fieldType}
              </span>
              {isModified && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-200 text-yellow-800">
                  Modified
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2">
          {field.fieldType === 'text' ? (
            <input
              type="text"
              value={val ?? ''}
              onChange={(e) => handleContentFieldChange(field.id, e.target.value)}
              className="input w-full"
              placeholder="Enter text..."
            />
          ) : field.fieldType === 'textarea' ? (
            <textarea
              value={val ?? ''}
              onChange={(e) => handleContentFieldChange(field.id, e.target.value)}
              className="textarea h-28 text-sm w-full"
              placeholder="Enter text..."
            />
          ) : field.fieldType === 'richText' ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                {isLexicalJson(val) && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">Lexical</span>
                )}
                <button
                  onClick={() => setCodeMode(prev => ({ ...prev, [field.id]: !prev[field.id] }))}
                  className={`text-xs px-2 py-1 rounded ${codeMode[field.id] ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  {codeMode[field.id] ? '</> Code' : 'Visual'}
                </button>
                <button
                  onClick={() => {
                    const isLex = isLexicalJson(val);
                    let plain = '';
                    if (isLex) {
                      plain = lexicalToPlainText(val);
                    } else if (typeof val === 'string') {
                      const tmp = document.createElement('div');
                      tmp.innerHTML = val;
                      plain = tmp.textContent || tmp.innerText || '';
                    }
                    if (plain.trim()) {
                      setRewriteFieldId(field.id);
                      setRewriteText(plain.trim());
                      setRewriteIsLexical(isLex);
                    }
                  }}
                  className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                >
                  Rewrite
                </button>
              </div>
              {val === null || val === undefined ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 italic text-sm">Empty</span>
                  <button onClick={() => handleContentFieldChange(field.id, '')} className="text-xs text-brand-600">Set value</button>
                </div>
              ) : codeMode[field.id] ? (
                <textarea
                  value={typeof val === 'string' ? val : JSON.stringify(val, null, 2)}
                  onChange={(e) => {
                    if (isLexicalJson(val)) {
                      try { handleContentFieldChange(field.id, JSON.parse(e.target.value)); } catch { /* let user keep typing */ }
                    } else {
                      handleContentFieldChange(field.id, e.target.value);
                    }
                  }}
                  className="textarea h-48 font-mono text-sm w-full bg-gray-900 text-green-400"
                />
              ) : isLexicalJson(val) ? (
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <ReactQuill
                    theme="snow"
                    value={lexicalToHtml(val)}
                    onChange={(content) => {
                      const stripped = content.replace(/<(.|\n)*?>/g, '').trim();
                      if (!stripped) {
                        handleContentFieldChange(field.id, htmlToLexical(''));
                      } else {
                        handleContentFieldChange(field.id, htmlToLexical(content));
                      }
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    style={{ minHeight: '150px' }}
                  />
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <ReactQuill
                    theme="snow"
                    value={typeof val === 'string' ? val : ''}
                    onChange={(content) => handleContentFieldChange(field.id, content)}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    style={{ minHeight: '150px' }}
                  />
                </div>
              )}
            </div>
          ) : field.fieldType === 'media' ? (
            <div className="space-y-3">
              {/* Preview */}
              {val ? (
                <div className="flex items-start gap-4">
                  {getMediaUrl(val) ? (
                    <img src={getMediaUrl(val)!} alt="Media preview" className="w-32 h-32 object-cover rounded-lg border border-gray-200" />
                  ) : typeof val === 'object' && val.filename ? (
                    <div className="w-32 h-32 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-[120px]">{val.filename}</p>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded font-mono block mb-2">
                      ID: {typeof val === 'object' ? val.id || JSON.stringify(val) : val}
                    </span>
                    <button onClick={() => handleContentFieldChange(field.id, null)} className="text-xs text-danger-600 hover:underline">Remove media</button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 italic text-sm">No media selected</p>
              )}
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openMediaBrowser(field.id)}
                  className="btn-secondary btn-sm flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Browse Media
                </button>
                <label className="btn-primary btn-sm flex items-center gap-1.5 cursor-pointer">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  Upload New
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      setMediaFieldTarget(field.id);
                      handleMediaUpload(e);
                    }}
                  />
                </label>
              </div>
            </div>
          ) : field.fieldType === 'repeater' ? (
            <div>
              {Array.isArray(val) && val.length > 0 ? (
                <div className="space-y-3">
                  {val.map((item: any, idx: number) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-lg border text-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-semibold text-gray-600 bg-gray-200 px-2 py-0.5 rounded">Item {idx + 1}</span>
                      </div>
                      {typeof item === 'object' && item !== null ? (
                        <div className="space-y-3">
                          {Object.entries(item).filter(([k]) => k !== 'id' && k !== 'blockType').map(([k, v]) => {
                            // Resolve typed sub-field: {fieldType: "text", value: "..."} or {fieldType: "richText", richTextValue: {...}}
                            const vObj = v as any;
                            const isTypedSubField = vObj && typeof vObj === 'object' && !Array.isArray(vObj) && vObj.fieldType;
                            const subFieldType = isTypedSubField ? vObj.fieldType : null;
                            let resolvedVal: any;
                            if (isTypedSubField) {
                              const typeColMap: Record<string, string> = { richText: 'richTextValue', textarea: 'textareaValue', media: 'mediaValue', select: 'selectValue', checkbox: 'checkboxValue' };
                              const col = typeColMap[subFieldType];
                              resolvedVal = (col && vObj[col] !== undefined && vObj[col] !== null) ? vObj[col] : vObj.value;
                            } else {
                              resolvedVal = v;
                            }

                            // Handler to update sub-field value back into the typed structure
                            const updateSubField = (newVal: any) => {
                              const newArr = [...val];
                              if (isTypedSubField) {
                                const typeColMap: Record<string, string> = { richText: 'richTextValue', textarea: 'textareaValue', media: 'mediaValue', select: 'selectValue', checkbox: 'checkboxValue' };
                                const col = typeColMap[subFieldType];
                                if (col) {
                                  newArr[idx] = { ...newArr[idx], [k]: { ...vObj, [col]: newVal, value: null } };
                                } else {
                                  newArr[idx] = { ...newArr[idx], [k]: { ...vObj, value: newVal } };
                                }
                              } else {
                                newArr[idx] = { ...newArr[idx], [k]: newVal };
                              }
                              handleContentFieldChange(field.id, newArr);
                            };

                            const subLabel = k.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                            return (
                              <div key={k} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-600">{subLabel}</span>
                                  {isTypedSubField && (
                                    <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${
                                      subFieldType === 'richText' ? 'bg-purple-100 text-purple-600' :
                                      subFieldType === 'textarea' ? 'bg-blue-100 text-blue-600' :
                                      subFieldType === 'media' ? 'bg-green-100 text-green-600' :
                                      'bg-gray-100 text-gray-500'
                                    }`}>{subFieldType}</span>
                                  )}
                                </div>
                                {/* Render by resolved sub-field type */}
                                {subFieldType === 'richText' ? (
                                  <div>
                                    <div className="flex items-center gap-1 mb-1">
                                      {isLexicalJson(resolvedVal) && (
                                        <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-600">Lexical</span>
                                      )}
                                      <button
                                        onClick={() => setCodeMode(prev => ({ ...prev, [`${field.id}_${idx}_${k}`]: !prev[`${field.id}_${idx}_${k}`] }))}
                                        className={`text-[10px] px-1.5 py-0.5 rounded ${codeMode[`${field.id}_${idx}_${k}`] ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'}`}
                                      >
                                        {codeMode[`${field.id}_${idx}_${k}`] ? '</>' : 'Visual'}
                                      </button>
                                    </div>
                                    {resolvedVal === null || resolvedVal === undefined ? (
                                      <span className="text-gray-400 italic text-xs">Empty</span>
                                    ) : codeMode[`${field.id}_${idx}_${k}`] ? (
                                      <textarea
                                        value={typeof resolvedVal === 'string' ? resolvedVal : JSON.stringify(resolvedVal, null, 2)}
                                        onChange={(e) => {
                                          if (isLexicalJson(resolvedVal)) {
                                            try { updateSubField(JSON.parse(e.target.value)); } catch {}
                                          } else {
                                            updateSubField(e.target.value);
                                          }
                                        }}
                                        className="textarea h-32 font-mono text-xs w-full bg-gray-900 text-green-400"
                                      />
                                    ) : isLexicalJson(resolvedVal) ? (
                                      <div className="border border-gray-300 rounded overflow-hidden bg-white">
                                        <ReactQuill
                                          theme="snow"
                                          value={lexicalToHtml(resolvedVal)}
                                          onChange={(content) => {
                                            const stripped = content.replace(/<(.|\n)*?>/g, '').trim();
                                            updateSubField(stripped ? htmlToLexical(content) : htmlToLexical(''));
                                          }}
                                          modules={quillModules}
                                          formats={quillFormats}
                                          className="bg-white"
                                          style={{ minHeight: '100px' }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="border border-gray-300 rounded overflow-hidden bg-white">
                                        <ReactQuill
                                          theme="snow"
                                          value={typeof resolvedVal === 'string' ? resolvedVal : ''}
                                          onChange={(content) => updateSubField(content)}
                                          modules={quillModules}
                                          formats={quillFormats}
                                          className="bg-white"
                                          style={{ minHeight: '100px' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : subFieldType === 'media' ? (
                                  <div className="flex items-center gap-3">
                                    {typeof resolvedVal === 'string' && resolvedVal ? (
                                      <img src={resolvedVal} alt={k} className="w-16 h-16 object-cover rounded border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    ) : null}
                                    <input
                                      type="text"
                                      value={typeof resolvedVal === 'string' ? resolvedVal : (resolvedVal ? JSON.stringify(resolvedVal) : '')}
                                      onChange={(e) => updateSubField(e.target.value)}
                                      className="input input-sm flex-1 text-xs font-mono"
                                      placeholder="Media URL or ID..."
                                    />
                                  </div>
                                ) : subFieldType === 'textarea' ? (
                                  <textarea
                                    value={resolvedVal ?? ''}
                                    onChange={(e) => updateSubField(e.target.value)}
                                    className="textarea text-sm flex-1 h-20 w-full"
                                    placeholder="Enter text..."
                                  />
                                ) : typeof resolvedVal === 'string' && resolvedVal.length > 200 ? (
                                  <textarea
                                    value={resolvedVal}
                                    onChange={(e) => updateSubField(e.target.value)}
                                    className="textarea text-sm flex-1 h-20 w-full"
                                  />
                                ) : typeof resolvedVal === 'string' || typeof resolvedVal === 'number' ? (
                                  <input
                                    type="text"
                                    value={String(resolvedVal)}
                                    onChange={(e) => updateSubField(e.target.value)}
                                    className="input input-sm flex-1 text-sm w-full"
                                  />
                                ) : resolvedVal === null || resolvedVal === undefined ? (
                                  <span className="text-gray-400 italic text-xs">Empty</span>
                                ) : (
                                  <textarea
                                    value={JSON.stringify(resolvedVal, null, 2)}
                                    onChange={(e) => { try { updateSubField(JSON.parse(e.target.value)); } catch {} }}
                                    className="textarea h-24 font-mono text-xs w-full"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-600">{String(item)}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400 italic text-sm">Empty repeater</span>
              )}
            </div>
          ) : (
            // Fallback: simple input
            val === null || val === undefined ? (
              <span className="text-gray-400 italic text-sm">Empty</span>
            ) : typeof val === 'object' ? (
              <textarea
                value={JSON.stringify(val, null, 2)}
                onChange={(e) => { try { handleContentFieldChange(field.id, JSON.parse(e.target.value)); } catch {} }}
                className="textarea h-32 font-mono text-xs w-full"
              />
            ) : (
              <input
                type="text"
                value={String(val)}
                onChange={(e) => handleContentFieldChange(field.id, e.target.value)}
                className="input w-full"
              />
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <style>{quillStyles}</style>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <button onClick={() => {
                  if (step === 'editor') setStep('pages');
                  else if (step === 'pages') navigate(-1);
                  else navigate(-1);
                }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Go back">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <h1 className="page-title">Payload CMS Agent</h1>
                {projectName && <span className="px-3 py-1 bg-brand-100 text-brand-700 rounded-full text-sm font-medium">{projectName}</span>}
              </div>
              <p className="page-subtitle">
                Connect to Payload CMS and update page content for your tenant
                <a href="https://drive.google.com/file/d/1DgmisB1ociVA0Z1z9_8Lc6yOMPG2o0jm/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
                {credential?.cms_url && <span className="ml-2 text-brand-600">| Connected to {credential.cms_url.replace('https://', '')}</span>}
              </p>
            </div>
            {credential && step !== 'credentials' && (
              <div className="flex items-center gap-3">
                <button onClick={loadHistory} className="btn-secondary">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  History
                </button>
                <button onClick={() => setStep('credentials')} className="btn-ghost text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Settings
                </button>
              </div>
            )}
          </div>
          {step !== 'credentials' && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <button onClick={() => { setStep('pages'); loadPages(1); }} className={`px-3 py-1 rounded-full ${step === 'pages' ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                1. Select Page
              </button>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <span className={`px-3 py-1 rounded-full ${step === 'editor' ? 'bg-brand-100 text-brand-700' : 'bg-gray-50 text-gray-400'}`}>
                2. Edit Fields
              </span>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="text-danger-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-danger-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Payload CMS Connection</h2>
                    <p className="text-gray-500">Connect to your Payload CMS instance with a tenant ID</p>
                  </div>
                </div>
                {credential && (
                  <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <span className="font-medium text-success-800">Connected to {credential.cms_url} (Tenant: {credential.tenant_id})</span>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="label">CMS URL</label>
                    <input type="url" value={cmsUrl} onChange={(e) => setCmsUrl(e.target.value)} placeholder="https://cms-staging.brandnorth.dev" className="input" disabled={testing || saving} />
                  </div>
                  <div>
                    <label className="label">API Key <span className="text-xs text-gray-400 ml-2">(Optional)</span></label>
                    <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Bearer token (leave blank if not required)" className="input font-mono" disabled={testing || saving} />
                  </div>
                  <div>
                    <label className="label">Tenant ID</label>
                    <input type="text" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="Enter your Payload tenant ID" className="input" disabled={testing || saving} />
                  </div>
                  {testResult && (
                    <div className={`p-4 rounded-lg ${testResult.success ? 'bg-success-50 border border-success-200' : 'bg-danger-50 border border-danger-200'}`}>
                      <div className="flex items-center gap-2">
                        {testResult.success
                          ? <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          : <svg className="w-5 h-5 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                        <span className={testResult.success ? 'text-success-800' : 'text-danger-800'}>{testResult.message}</span>
                      </div>
                      {testResult.total_pages != null && <p className="mt-2 text-sm text-success-700">Pages found: {testResult.total_pages}</p>}
                    </div>
                  )}
                  <div className="flex gap-3 pt-4">
                    <button onClick={testConnection} disabled={testing || !cmsUrl || !tenantId} className="btn-secondary flex-1">
                      {testing ? <><span className="spinner"></span>Testing...</> : <>Test Connection</>}
                    </button>
                    <button onClick={saveCredentials} disabled={saving || !testResult?.success} className="btn-primary flex-1">
                      {saving ? <><span className="spinner"></span>Saving...</> : <>Save & Continue</>}
                    </button>
                  </div>
                  {credential && (
                    <div className="pt-4 border-t border-gray-200">
                      <button onClick={deleteCredentials} className="btn-danger w-full">Delete Credentials</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Pages List */}
        {step === 'pages' && (
          <div className="max-w-6xl mx-auto">
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Payload CMS Pages</h2>
                    <p className="text-sm text-gray-500 mt-1">{totalDocs} pages for tenant {credential?.tenant_id} &mdash; Click to edit</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <div className="flex-1 relative">
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadPages(1, searchTerm)} placeholder="Search pages by title..." className="input pl-10" />
                    <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  </div>
                  <button onClick={() => loadPages(1, searchTerm)} className="btn-secondary">Search</button>
                </div>
              </div>
              <div className="card-body">
                {loadingPages ? (
                  <div className="flex items-center justify-center py-12"><span className="spinner mr-2"></span><span className="text-gray-500">Loading pages...</span></div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {pages.map((page) => (
                        <div key={page.id} onClick={() => openEditor(page)} className="p-4 border-2 border-gray-200 rounded-lg cursor-pointer transition-all hover:border-brand-300 hover:bg-gray-50">
                          <h4 className="font-medium text-gray-900 truncate">{page.title}</h4>
                          {page.slug && <p className="text-xs text-gray-500 mt-1 truncate">/{page.slug}/</p>}
                          <div className="flex items-center gap-2 mt-2">
                            {page.status && <span className={`px-2 py-0.5 rounded text-xs font-medium ${page.status === 'published' ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-600'}`}>{page.status}</span>}
                          </div>
                          {page.updated_at && <p className="text-xs text-gray-400 mt-2">Updated: {new Date(page.updated_at).toLocaleDateString()}</p>}
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-center gap-2">
                        <button onClick={() => loadPages(currentPage - 1, searchTerm)} disabled={currentPage === 1} className="btn-secondary btn-sm">Previous</button>
                        <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                        <button onClick={() => loadPages(currentPage + 1, searchTerm)} disabled={currentPage === totalPages} className="btn-secondary btn-sm">Next</button>
                      </div>
                    )}
                    {pages.length === 0 && <div className="text-center py-12 text-gray-500"><p>No pages found</p></div>}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Structured Editor */}
        {step === 'editor' && editingPage && (
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Edit: {editingPage.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Page ID: {editingPage.id} | {pageDetail?.field_count ?? 0} content fields
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {hasChanges && <span className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">{modifiedFieldCount} unsaved</span>}
                    <button onClick={savePageFields} disabled={savingFields || !hasChanges} className="btn-primary">
                      {savingFields ? <><span className="spinner"></span>Saving...</> : <>Save to Payload CMS</>}
                    </button>
                    <button onClick={closeEditor} className="btn-secondary">Back to Pages</button>
                  </div>
                </div>

                {/* Editor Tabs */}
                <div className="mt-4 flex gap-1 border-b border-gray-200">
                  {(['content', 'layout', 'seo', 'code'] as EditorTab[]).map(tab => {
                    if (tab === 'layout' && layoutBlocks.length === 0) return null;
                    return (
                      <button
                        key={tab}
                        onClick={() => setEditorTab(tab)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                          editorTab === tab
                            ? 'bg-white border border-b-white border-gray-200 text-brand-700 -mb-px'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab === 'content' ? `Content Fields (${contentFields.length})` :
                         tab === 'layout' ? `Layout Blocks (${layoutBlocks.length})` :
                         tab === 'seo' ? 'SEO' :
                         'Custom CSS / JS'}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Alerts */}
            {editorError && (
              <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-danger-700">{editorError}</span>
              </div>
            )}
            {editorSuccess && (
              <div className="mb-4 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span className="text-success-700">{editorSuccess}</span>
              </div>
            )}

            {loadingFields ? (
              <div className="card"><div className="card-body flex items-center justify-center py-12"><span className="spinner mr-2"></span><span className="text-gray-500">Loading page fields...</span></div></div>
            ) : (
              <>
                {/* Content Fields Tab */}
                {editorTab === 'content' && (
                  <div className="flex gap-4">
                    {/* Group sidebar */}
                    {pageDetail && pageDetail.field_groups.length > 1 && (
                      <div className="w-56 flex-shrink-0">
                        <div className="card sticky top-4">
                          <div className="card-body p-3">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">Field Groups</h4>
                            <button
                              onClick={() => setActiveGroup('all')}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeGroup === 'all' ? 'bg-brand-100 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                              All Fields ({contentFields.length})
                            </button>
                            {pageDetail.field_groups.map(group => (
                              <button
                                key={group}
                                onClick={() => setActiveGroup(group)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeGroup === group ? 'bg-brand-100 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                              >
                                {group} ({contentFields.filter(f => f.field_group_title === group).length})
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fields */}
                    <div className="flex-1 min-w-0">
                      {/* Page Title & Slug */}
                      <div className="card mb-4">
                        <div className="card-body">
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Page Settings</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="label">Title {hasTitleChange && <span className="text-yellow-600 text-xs ml-1">Modified</span>}</label>
                              <input type="text" value={pageTitle} onChange={(e) => setPageTitle(e.target.value)} className="input w-full" />
                            </div>
                            <div>
                              <label className="label">Slug {hasSlugChange && <span className="text-yellow-600 text-xs ml-1">Modified</span>}</label>
                              <input type="text" value={pageSlug} onChange={(e) => setPageSlug(e.target.value)} className="input w-full" />
                            </div>
                          </div>
                          {(pageSettings.stickyHeader !== undefined || pageSettings.showBackToTop !== undefined) && (
                            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
                              {pageSettings.stickyHeader !== undefined && (
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={pageSettings.stickyHeader ?? false}
                                    onChange={(e) => setPageSettings(prev => ({ ...prev, stickyHeader: e.target.checked }))}
                                    className="rounded border-gray-300"
                                  />
                                  Sticky Header
                                  {pageSettings.stickyHeader !== originalPageSettings.stickyHeader && <span className="text-yellow-600 text-xs">Modified</span>}
                                </label>
                              )}
                              {pageSettings.showBackToTop !== undefined && (
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={pageSettings.showBackToTop ?? false}
                                    onChange={(e) => setPageSettings(prev => ({ ...prev, showBackToTop: e.target.checked }))}
                                    className="rounded border-gray-300"
                                  />
                                  Show Back to Top
                                  {pageSettings.showBackToTop !== originalPageSettings.showBackToTop && <span className="text-yellow-600 text-xs">Modified</span>}
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content fields list */}
                      <div className="space-y-3">
                        {visibleFields.length === 0 ? (
                          <div className="card"><div className="card-body text-center py-12 text-gray-500">No fields in this group</div></div>
                        ) : (
                          visibleFields.map(field => renderFieldEditor(field))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Layout Blocks Tab */}
                {editorTab === 'layout' && layoutBlocks.length > 0 && (
                  <div className="space-y-4">
                    <div className="card">
                      <div className="card-body">
                        <p className="text-sm text-gray-500 mb-4">
                          This page has {layoutBlocks.length} layout block(s). Each block represents a component on the page.
                          {hasLayoutChanges && <span className="ml-2 text-yellow-600 font-medium">Layout has been modified</span>}
                        </p>
                      </div>
                    </div>
                    {layoutBlocks.map((block, blockIdx) => {
                      const isExpanded = expandedBlocks[block.id] ?? false;
                      const blockModified = JSON.stringify(block) !== JSON.stringify(originalLayoutBlocks[blockIdx]);
                      const blockLabel = block.blockName || block.blockType;

                      // Separate simple fields from complex ones
                      const simpleFields: [string, any][] = [];
                      const richTextFields: [string, any][] = [];
                      const complexFields: [string, any][] = [];
                      const boolFields: [string, any][] = [];
                      const colorFields: [string, any][] = [];

                      Object.entries(block.data).forEach(([key, val]) => {
                        if (typeof val === 'boolean') boolFields.push([key, val]);
                        else if (typeof val === 'string' && key.toLowerCase().includes('color')) colorFields.push([key, val]);
                        else if (typeof val === 'string' && val.length < 500) simpleFields.push([key, val]);
                        else if (typeof val === 'string') simpleFields.push([key, val]);
                        else if (isLexicalJson(val)) richTextFields.push([key, val]);
                        else if (val !== null && val !== undefined) complexFields.push([key, val]);
                      });

                      const handleBlockFieldChange = (key: string, newVal: any) => {
                        setLayoutBlocks(prev => prev.map((b, i) =>
                          i === blockIdx ? { ...b, data: { ...b.data, [key]: newVal } } : b
                        ));
                      };

                      return (
                        <div key={block.id} className={`rounded-lg border ${blockModified ? 'border-yellow-300 bg-yellow-50/30' : 'border-gray-200 bg-white'}`}>
                          {/* Block header */}
                          <button
                            onClick={() => setExpandedBlocks(prev => ({ ...prev, [block.id]: !isExpanded }))}
                            className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors rounded-t-lg"
                          >
                            <div className="flex items-center gap-3">
                              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <span className="font-semibold text-gray-900">{blockLabel}</span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">{block.blockType}</span>
                              {blockModified && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-200 text-yellow-800">Modified</span>}
                            </div>
                            <span className="text-xs text-gray-400">{Object.keys(block.data).length} fields</span>
                          </button>

                          {isExpanded && (
                            <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
                              {/* Boolean toggles */}
                              {boolFields.length > 0 && (
                                <div className="flex flex-wrap gap-4 pt-4">
                                  {boolFields.map(([key, val]) => (
                                    <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={val ?? false}
                                        onChange={(e) => handleBlockFieldChange(key, e.target.checked)}
                                        className="rounded border-gray-300"
                                      />
                                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                                    </label>
                                  ))}
                                </div>
                              )}

                              {/* Color fields */}
                              {colorFields.length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                                  {colorFields.map(([key, val]) => (
                                    <div key={key}>
                                      <label className="text-xs text-gray-500 block mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="text"
                                          value={val ?? ''}
                                          onChange={(e) => handleBlockFieldChange(key, e.target.value)}
                                          className="input input-sm flex-1 text-sm font-mono"
                                          placeholder="#000000"
                                        />
                                        {val && (
                                          <div className="w-7 h-7 rounded border border-gray-300 flex-shrink-0" style={{ backgroundColor: val }} />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Simple text fields */}
                              {simpleFields.map(([key, val]) => (
                                <div key={key} className="pt-2">
                                  <label className="text-xs text-gray-500 font-medium block mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                                  {typeof val === 'string' && val.length > 200 ? (
                                    <textarea
                                      value={val}
                                      onChange={(e) => handleBlockFieldChange(key, e.target.value)}
                                      className="textarea h-24 text-sm w-full"
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={val ?? ''}
                                      onChange={(e) => handleBlockFieldChange(key, e.target.value)}
                                      className="input w-full text-sm"
                                    />
                                  )}
                                </div>
                              ))}

                              {/* RichText (Lexical) fields */}
                              {richTextFields.map(([key, val]) => {
                                const blockFieldId = `${block.id}_${key}`;
                                return (
                                  <div key={key} className="pt-2">
                                    <div className="flex items-center gap-2 mb-1">
                                      <label className="text-xs text-gray-500 font-medium">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">richText</span>
                                      <button
                                        onClick={() => setCodeMode(prev => ({ ...prev, [blockFieldId]: !prev[blockFieldId] }))}
                                        className={`text-xs px-2 py-0.5 rounded ${codeMode[blockFieldId] ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
                                      >
                                        {codeMode[blockFieldId] ? '</>' : 'Visual'}
                                      </button>
                                    </div>
                                    {codeMode[blockFieldId] ? (
                                      <textarea
                                        value={JSON.stringify(val, null, 2)}
                                        onChange={(e) => {
                                          try { handleBlockFieldChange(key, JSON.parse(e.target.value)); } catch { /* let user keep typing */ }
                                        }}
                                        className="textarea h-40 font-mono text-sm w-full bg-gray-900 text-green-400"
                                      />
                                    ) : (
                                      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                                        <ReactQuill
                                          theme="snow"
                                          value={lexicalToHtml(val)}
                                          onChange={(content) => handleBlockFieldChange(key, htmlToLexical(content))}
                                          modules={quillModules}
                                          formats={quillFormats}
                                          className="bg-white"
                                          style={{ minHeight: '120px' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              {/* Complex fields (JSON editor) */}
                              {complexFields.map(([key, val]) => (
                                <div key={key} className="pt-2">
                                  <div className="flex items-center gap-2 mb-1">
                                    <label className="text-xs text-gray-500 font-medium">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                                      {Array.isArray(val) ? `array (${val.length})` : 'object'}
                                    </span>
                                  </div>
                                  <textarea
                                    value={JSON.stringify(val, null, 2)}
                                    onChange={(e) => {
                                      try { handleBlockFieldChange(key, JSON.parse(e.target.value)); } catch { /* let user keep typing */ }
                                    }}
                                    className="textarea font-mono text-xs w-full bg-gray-900 text-green-400"
                                    style={{ minHeight: `${Math.min(300, Math.max(80, JSON.stringify(val, null, 2).split('\n').length * 18))}px` }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SEO Tab */}
                {editorTab === 'seo' && (
                  <div className="card">
                    <div className="card-body">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Search Engine Optimization</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="label">SEO Title</label>
                          <input type="text" value={seoFields.title ?? ''} onChange={(e) => handleSeoChange('title', e.target.value)} className="input w-full" placeholder="Page title for search engines" />
                          <p className="text-xs text-gray-400 mt-1">{(seoFields.title ?? '').length} / 60 characters</p>
                        </div>
                        <div>
                          <label className="label">Meta Description</label>
                          <textarea value={seoFields.description ?? ''} onChange={(e) => handleSeoChange('description', e.target.value)} className="textarea h-24 w-full" placeholder="Meta description for search engines" />
                          <p className="text-xs text-gray-400 mt-1">{(seoFields.description ?? '').length} / 160 characters</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={seoFields.noIndex ?? false} onChange={(e) => handleSeoChange('noIndex', e.target.checked)} className="rounded border-gray-300" />
                          <label className="text-sm text-gray-700">No Index (hide from search engines)</label>
                        </div>
                        {hasSeoChange && <p className="text-sm text-yellow-600">SEO fields have been modified</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Code Tab */}
                {editorTab === 'code' && (
                  <div className="space-y-4">
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Custom CSS</h3>
                          {hasCSSChange && <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">Modified</span>}
                        </div>
                        <textarea
                          value={customCSS}
                          onChange={(e) => setCustomCSS(e.target.value)}
                          className="textarea font-mono text-sm w-full bg-gray-900 text-green-400 rounded-lg"
                          style={{ minHeight: '300px' }}
                          placeholder="/* Custom CSS for this page */"
                        />
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Custom JavaScript</h3>
                          {hasJSChange && <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">Modified</span>}
                        </div>
                        <textarea
                          value={customJS}
                          onChange={(e) => setCustomJS(e.target.value)}
                          className="textarea font-mono text-sm w-full bg-gray-900 text-yellow-400 rounded-lg"
                          style={{ minHeight: '200px' }}
                          placeholder="// Custom JavaScript for this page"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Sticky Save Footer */}
                <div className="sticky bottom-0 mt-4 bg-white border border-gray-200 rounded-xl p-4 flex justify-between items-center shadow-lg">
                  <div className="text-sm text-gray-500">{modifiedFieldCount} field(s) modified</div>
                  <div className="flex items-center gap-3">
                    <button onClick={closeEditor} className="btn-secondary">Cancel</button>
                    <button onClick={savePageFields} disabled={savingFields || !hasChanges} className="btn-primary">
                      {savingFields ? 'Saving...' : 'Save to Payload CMS'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Media Browser Modal */}
        {showMediaBrowser && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => { setShowMediaBrowser(false); setMediaFieldTarget(null); }}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Select Media</h3>
                    <button onClick={() => { setShowMediaBrowser(false); setMediaFieldTarget(null); }} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="btn-primary btn-sm flex items-center gap-1.5 cursor-pointer">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      {mediaUploading ? 'Uploading...' : 'Upload New Image'}
                      <input type="file" accept="image/*" className="hidden" disabled={mediaUploading} onChange={handleMediaUpload} />
                    </label>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={mediaSearch}
                        onChange={(e) => setMediaSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadMedia(1, mediaSearch)}
                        placeholder="Search media..."
                        className="input pl-10 w-full"
                      />
                      <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    </div>
                    <button onClick={() => loadMedia(1, mediaSearch)} className="btn-secondary btn-sm">Search</button>
                  </div>
                </div>
                <div className="p-6">
                  {mediaLoading ? (
                    <div className="flex items-center justify-center py-12"><span className="spinner mr-2"></span><span className="text-gray-500">Loading media...</span></div>
                  ) : mediaItems.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No media found. Upload an image to get started.</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {mediaItems.map((item: any) => {
                          const thumbUrl = item.url || (item.filename && credential ? `${credential.cms_url}/media/${item.filename}` : null);
                          return (
                            <div
                              key={item.id}
                              onClick={() => selectMediaItem(item)}
                              className="group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-brand-400 transition-colors"
                            >
                              {thumbUrl ? (
                                <div className="aspect-square bg-gray-100">
                                  <img src={thumbUrl} alt={item.filename || 'Media'} className="w-full h-full object-cover" />
                                </div>
                              ) : (
                                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                  <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                              )}
                              <div className="p-2">
                                <p className="text-xs text-gray-700 truncate font-medium">{item.filename || item.alt || `ID: ${item.id}`}</p>
                                {item.mimeType && <p className="text-[10px] text-gray-400">{item.mimeType}</p>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {mediaTotalPages > 1 && (
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <button onClick={() => loadMedia(mediaPage - 1, mediaSearch)} disabled={mediaPage === 1} className="btn-secondary btn-sm">Previous</button>
                          <span className="text-sm text-gray-500">Page {mediaPage} of {mediaTotalPages}</span>
                          <button onClick={() => loadMedia(mediaPage + 1, mediaSearch)} disabled={mediaPage === mediaTotalPages} className="btn-secondary btn-sm">Next</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* History Modal */}
        {showHistory && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowHistory(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Update History</h3>
                  <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="p-6">
                  {history.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No update history yet</div>
                  ) : (
                    <div className="space-y-4">
                      {history.map((session) => (
                        <div key={session.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{session.page_title || `Page ${session.page_id}`}</p>
                              <p className="text-sm text-gray-500">{session.action}</p>
                              {session.fields_updated && <p className="text-xs text-gray-400 mt-1">Fields: {session.fields_updated.join(', ')}</p>}
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${session.status === 'success' ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'}`}>{session.status}</span>
                              <p className="text-xs text-gray-400 mt-2">{new Date(session.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          {session.error_message && <p className="text-sm text-danger-600 mt-2">{session.error_message}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {/* Rewrite Modal */}
        <RewriteModal
          isOpen={rewriteFieldId !== null}
          selectedText={rewriteText}
          onClose={() => { setRewriteFieldId(null); setRewriteText(''); setRewriteIsLexical(false); }}
          onAccept={(rewrittenText) => {
            if (rewriteFieldId) {
              if (rewriteIsLexical) {
                const html = rewrittenText.split('\n').filter(Boolean).map(p => `<p>${p}</p>`).join('');
                handleContentFieldChange(rewriteFieldId, htmlToLexical(html));
              } else {
                const wrapped = rewrittenText.split('\n').filter(Boolean).map(p => `<p>${p}</p>`).join('');
                handleContentFieldChange(rewriteFieldId, wrapped);
              }
            }
            setRewriteFieldId(null);
            setRewriteText('');
            setRewriteIsLexical(false);
          }}
        />
      </div>
    </Layout>
  );
}
